import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getDestinationRegionLabel } from '@/features/destinations/utils/DestinationRegionLabelMapper';
import { useRelevanceSearch } from '@/features/relevanceSearch/api/getRelevanceSearch';
import useAppointmentStore from '@/store/AppointmentStore';
import useContextAwarenessStore from '@/store/ContextAwarenessStore';
import useFeatureFlagsStore from '@/store/FeatureFlagsStore';
import type { FlowType } from '@/types/FlowType';
import { blastDataToLogCannon } from '@/utils/blastDataToLogCannon';
import { formatSearchedDestination } from '@/utils/formatSearchedDestination';
import useProceedWithDestination from './useProceedWithDestination';

const getNormalizedQueryParam = (searchParams: URLSearchParams, key: string) =>
  searchParams.get(key)?.trim() ?? '';

type DeepLinkResolutionStatus = 'idle' | 'resolving' | 'done';

// Single owner of context-awareness URL params:
//   - card / sourceUrl are persisted as marketing attribution
//   - ?dest= is resolved against destinations and proceeds straight into
//     scheduling; callers render a loader while `isResolving` is true.
const useContextAwarenessParams = (flow: FlowType) => {
  const [searchParams] = useSearchParams();
  const hasStoredAttribution = useRef(false);
  const hasLoggedContextAwareness = useRef(false);

  const setContextAwareness = useContextAwarenessStore(
    (state) => state.setContextAwareness,
  );
  const setSelectedDestination = useAppointmentStore(
    (state) => state.setSelectedDestination,
  );
  const setSearchedDestination = useAppointmentStore(
    (state) => state.setSearchedDestination,
  );
  const destinations = useAppointmentStore((state) => state.destinations);
  const useNewDestinations = useFeatureFlagsStore(
    (state) => state.featureFlags.useNewDestinations,
  );
  const proceedWithDestination = useProceedWithDestination(flow);

  const destinationQuery = useMemo(
    () => getNormalizedQueryParam(searchParams, 'dest'),
    [searchParams],
  );
  const [deepLinkResolutionStatus, setDeepLinkResolutionStatus] =
    useState<DeepLinkResolutionStatus>(() =>
      destinationQuery ? 'resolving' : 'idle',
    );

  const contextAwareness = useMemo(
    () => ({
      card: getNormalizedQueryParam(searchParams, 'card'),
      sourceUrl: getNormalizedQueryParam(searchParams, 'sourceUrl'),
    }),
    [searchParams],
  );

  const relevanceSearchQuery = useRelevanceSearch(
    destinationQuery,
    useNewDestinations,
  );

  // Log context awareness parameters for Splunk tracking
  useEffect(() => {
    if (hasLoggedContextAwareness.current) return;

    if (
      contextAwareness.card ||
      contextAwareness.sourceUrl ||
      destinationQuery
    ) {
      hasLoggedContextAwareness.current = true;
      blastDataToLogCannon(
        {
          card: contextAwareness.card,
          sourceUrl: contextAwareness.sourceUrl,
          dest: destinationQuery,
        },
        'TRVL-CONTEXT-AWARENESS',
      );
    }
  }, [contextAwareness.card, contextAwareness.sourceUrl, destinationQuery]);

  const isResolving = deepLinkResolutionStatus === 'resolving';

  useEffect(() => {
    if (hasStoredAttribution.current) return;
    hasStoredAttribution.current = true;
    // Don't wipe stored values when remounting on a route without params.
    if (!contextAwareness.card && !contextAwareness.sourceUrl) return;
    setContextAwareness(contextAwareness);
  }, [contextAwareness, setContextAwareness]);

  useEffect(() => {
    if (destinationQuery && deepLinkResolutionStatus === 'idle') {
      setDeepLinkResolutionStatus('resolving');
    }
  }, [deepLinkResolutionStatus, destinationQuery]);

  useEffect(() => {
    if (deepLinkResolutionStatus !== 'resolving' || !destinationQuery) return;
    if (relevanceSearchQuery.isPending || !destinations.length) return;

    const resolvedResult = relevanceSearchQuery.data?.[0];
    const resolvedDestination = resolvedResult
      ? destinations.find((d) => d.name === resolvedResult.aceDestination)
      : undefined;

    if (!resolvedResult || !resolvedDestination) {
      setDeepLinkResolutionStatus('done');
      return;
    }

    const regionLabel = getDestinationRegionLabel(
      resolvedDestination.name,
      useNewDestinations,
    );

    setSelectedDestination({
      ...resolvedDestination,
      name: regionLabel,
    });
    setSearchedDestination(
      formatSearchedDestination([
        resolvedResult.name,
        resolvedResult.region,
        resolvedResult.subRegion,
        resolvedResult.country,
      ]),
    );
    proceedWithDestination(resolvedDestination);
  }, [
    deepLinkResolutionStatus,
    destinationQuery,
    destinations,
    proceedWithDestination,
    relevanceSearchQuery.data,
    relevanceSearchQuery.isPending,
    setSearchedDestination,
    setSelectedDestination,
    useNewDestinations,
  ]);

  return { isResolving };
};

export default useContextAwarenessParams;
